<template>
  <div id="app-config-container">
    <h2>{{ $t('adminPanel.appConfiguration') }}</h2>

    <div id="checkbot-timeout">
      <div v-if="checkBotTimeout !== null" id="timeout-input-container">
        <label for="checkbot-timeout-input">{{
          $t('adminPanel.checkBotTimeout')
        }}</label>

        <PVInputNumber
          id="checkbot-timeout-input"
          v-model="checkBotTimeout"
          name="checkbot-timeout-input "
          :min="0"
        />
      </div>

      <vf-button
        :size="buttonSizes.large"
        :variant="buttonVariants.primary"
        @click="setCheckBotTimeout"
      >
        {{ $t('adminPanel.saveChanges') }}
      </vf-button>
    </div>

    <div id="run-check-buttons">
      <vf-button
        :busy="isCheckingCheckBotResuls"
        :size="buttonSizes.medium"
        :variant="buttonVariants.secondary"
        @click="checkCheckBotResults"
      >
        {{ $t('adminPanel.checkCheckBotResults') }}
      </vf-button>

      <vf-button
        :busy="isCheckingTimeouts"
        :size="buttonSizes.medium"
        :variant="buttonVariants.secondary"
        @click="checkCheckBotTimeouts"
      >
        {{ $t('adminPanel.checkCheckBotTimeouts') }}
      </vf-button>
    </div>
  </div>
</template>

<script lang="ts">
  import axios from 'axios';
  import { defineComponent } from 'vue';
  import endpointsList from '../../helpers/endpointsList';
  import { ToastMixin } from '../../mixins/globalUtils';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import { ButtonVariant } from '../VfButton/VfButton.types';

  interface IAppConfig {
    buttonVariant: ButtonVariant.SECONDARY;
    checkBotTimeout: number | null;
    isCheckingCheckBotResuls: boolean;
    isCheckingTimeouts: boolean;
    createToast?: ToastMixin;
  }

  export default defineComponent({
    name: 'AppConfig',

    components: { VfButton },

    data(): IAppConfig {
      return {
        buttonVariant: ButtonVariant.SECONDARY,
        checkBotTimeout: null,
        isCheckingCheckBotResuls: false,
        isCheckingTimeouts: false,
      };
    },

    mounted() {
      this.getCheckBotTimeout();
    },

    methods: {
      async getCheckBotTimeout() {
        try {
          const response = await axios.get(
            endpointsList.appConfig.getAppConfig,
          );

          const checkBotTimeout =
            response.data.checkBotTimeout ?? 365 * 24 * 60 * 60 * 1000;

          this.checkBotTimeout = Math.floor(
            checkBotTimeout / 1000 / 60 / 60 / 24,
          );
        } catch (err) {
          this.createToast!(
            this.$t('errors.error'),
            this.$t('errors.error'),
            'danger',
          );
        }
      },

      async setCheckBotTimeout() {
        const checkBotTimeout =
          Number(this.checkBotTimeout) * 24 * 60 * 60 * 1000;

        try {
          await axios.patch('app-config', { checkBotTimeout });

          this.createToast!(
            this.$t('appConfig.checkBotTimeoutSaved'),
            this.$t('appConfig.checkBotTimeoutSaved'),
            'success',
          );

          this.getCheckBotTimeout();
        } catch (err) {
          this.createToast!(
            this.$t('errors.error'),
            this.$t('errors.error'),
            'danger',
          );
        }
      },

      async checkCheckBotResults() {
        this.isCheckingCheckBotResuls = true;
        try {
          await axios.get(endpointsList.checkBot.checkCheckBotResults);
        } catch (err: unknown) {
          this.createToast!(
            this.$t('errors.error'),
            this.$t('adminPanel.errors.errorCheckingCheckBotResults'),
            'danger',
          );
        }

        this.isCheckingCheckBotResuls = false;
      },

      async checkCheckBotTimeouts() {
        this.isCheckingTimeouts = true;
        try {
          await axios.get(endpointsList.checkBot.checkCheckBotTimeouts);
        } catch (err: unknown) {
          this.createToast!(
            this.$t('errors.error'),
            this.$t('adminPanel.errors.errorCheckingCheckBotTimeouts'),
            'danger',
          );
        }

        this.isCheckingTimeouts = false;
      },
    },
  });
</script>

<style scoped lang="scss">
  #app-config-container {
    padding: $tab-view-margin-top 0;

    h2 {
      @include subheading;
      margin-bottom: 20px;
    }

    #checkbot-timeout {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      #timeout-input-container {
        label {
          font-weight: 500;
          margin-right: 1rem;
        }
      }
    }

    .spinner-container {
      width: 100%;
      height: 90vh;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    #run-check-buttons {
      margin-top: 50px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      row-gap: 10px;
    }
  }
</style>
